import { useEffect, useState, useCallback } from 'react';

export type UseHeaderUpdateSubscriptionArgs = {
  /**
   * Only subscribe to header-update events if the query matches this string
   * or matches a regular expression
   */
  operationNameFilter?: string | RegExp;
};

/**
 * This is the shape of the payload from app_express.  If app_express updates
 * the shape, then this type definition also needs to be updated.
 */
export type HeaderUpdateDetails = {
  operationName: string;
  variables: Record<string, unknown>;
  data: Record<string, unknown> | null | undefined;
};

export const useHeaderUpdateSubscription = ({
  operationNameFilter,
}: UseHeaderUpdateSubscriptionArgs = {}) => {
  const [pingCount, setPingCount] = useState<number>(0);

  const handleLoginLogoutEvents = useCallback(
    (event: CustomEvent<HeaderUpdateDetails>) => {
      const { operationName } = event.detail;

      function ping() {
        setPingCount((c) => c + 1);
      }

      // If the dev provided a value for query, then we only want to subscribe
      // to the event if it matches our filter or passes our filter regex test.
      if (operationNameFilter) {
        if (
          typeof operationNameFilter === 'string' &&
          operationName === operationNameFilter
        ) {
          ping();
        } else if (
          operationNameFilter instanceof RegExp &&
          operationNameFilter.test(operationName)
        ) {
          ping();
        }
      } else {
        ping();
      }
    },
    [operationNameFilter],
  );

  useEffect(() => {
    // Cast to EventListener to satisfy addEventListener signature
    // I don't like casting with " as" but this is how they say to do this for
    // custom events - M.T 4/21/2026
    document.addEventListener(
      'header-update',
      handleLoginLogoutEvents as EventListener,
      false,
    );

    return () => {
      document.removeEventListener(
        'header-update',
        handleLoginLogoutEvents as EventListener,
        false,
      );
    };
  }, [handleLoginLogoutEvents]);

  return pingCount;
};
